// Chemin: C:\gestion-copro\src\types\checklist.types.ts
// Types pour le Chantier 1 — SENTINELLE relance (checklist documentaire)
// Session 110
// Session 161 : Ajout attestation_immatriculation_rnc — alignement 16 docs SENTINELLE

// ============================================================================
// ITEM DE LA CHECKLIST
// ============================================================================

/** Niveau d'urgence d'un document manquant */
export type NiveauUrgence = 'critique' | 'important' | 'recommande';

/** Statut d'un item de la checklist */
export type StatutChecklistItem = 'depose' | 'manquant' | 'urgent' | 'perime';

/** Identifiant unique de chaque document attendu */
export type ChecklistItemId =
  // === Base commune (toute copropriété) ===
  | 'pv_derniere_ag'
  | 'reglement_copropriete'
  | 'contrat_syndic'
  | 'appel_fonds'
  | 'fiche_synthetique'
  | 'attestation_assurance_copro'
  // === Copropriété neuve (ajout) ===
  | 'pv_reception_parties_communes'
  | 'pv_livraison_lot'
  | 'notice_descriptive'
  | 'attestation_dommages_ouvrage'
  | 'carnet_entretien'
  // === Occupant (ajout si statutOccupation = occupant ou mixte) ===
  | 'assurance_habitation'
  // === Bailleur (ajout si statutOccupation = bailleur ou mixte) ===
  | 'assurance_pno'
  | 'bail_location'
  | 'diagnostics_locatifs'
  | 'etat_des_lieux'
  // === Immatriculation (toute copropriété) ===
  | 'attestation_immatriculation_rnc';

/** Un item de la checklist documentaire */
export interface ChecklistItem {
  id: ChecklistItemId;
  /** Libellé affiché à Nicole */
  label: string;
  /** Pourquoi ce document est important — affiché quand manquant */
  explication: string;
  /** Niveau d'urgence par défaut (peut être rehaussé dynamiquement, ex: GPA proche) */
  urgence: NiveauUrgence;
  /** Types de documents Firestore qui matchent cet item (champ `type` dans collection documents) */
  typesDocumentMatch: string[];
  /** Statut calculé dynamiquement */
  statut: StatutChecklistItem;
  /** ID du document Firestore trouvé (si déposé) */
  documentId?: string;
  /** Nom du fichier trouvé (si déposé) */
  documentTitre?: string;
  /** Date du document trouvé (si déposé) */
  documentDate?: Date;
  /** Conseil pour obtenir ce document si Nicole ne l'a pas */
  conseilObtention: string;
  /** Uniquement pour copropriété neuve ? */
  neuveOnly: boolean;
  /** Uniquement pour bailleur ou mixte ? */
  bailleurOnly: boolean;
  /** Uniquement pour occupant ou mixte ? */
  occupantOnly: boolean;
  /** Section d'affichage dans le widget */
  section: 'copropriete' | 'neuve' | 'bailleur' | 'occupant';
  /** Fraîcheur max en mois — si le document le plus récent est plus vieux, statut "périmé". null = pas de péremption */
  fraicheurMaxMois: number | null;
}

// ============================================================================
// ÉTAT GLOBAL DE LA CHECKLIST
// ============================================================================

/** État complet de la checklist pour une copropriété */
export interface ChecklistState {
  /** Liste des items (personnalisée selon profil) */
  items: ChecklistItem[];
  /** Nombre total d'items attendus */
  totalAttendus: number;
  /** Nombre de documents déposés (matchés) */
  totalDeposes: number;
  /** Nombre de documents manquants */
  totalManquants: number;
  /** Taux de complétude (0-100) */
  tauxCompletude: number;
  /** Y a-t-il au moins un document critique manquant ? */
  alerteCritique: boolean;
  /** Date de calcul */
  dateCalcul: Date;
}

// ============================================================================
// RELANCES
// ============================================================================

/** Paliers de relance (jours après onboarding) */
export const PALIERS_RELANCE = [3, 10, 21] as const;
/** Au-delà de J+21, relance mensuelle (tous les 30 jours) */
export const RELANCE_MENSUELLE_JOURS = 30;

/** État de relance stocké en Firestore */
export interface RelanceState {
  /** Date de la dernière relance affichée */
  derniereRelance: Date | null;
  /** Nombre de relances déjà effectuées */
  nombreRelances: number;
  /** Items déjà signalés comme manquants lors de la dernière relance */
  itemsSignales: ChecklistItemId[];
  /** Nicole a-t-elle cliqué "Je ne l'ai pas" sur certains items ? */
  itemsNonDisponibles: ChecklistItemId[];
}

// ============================================================================
// INTERLOCUTEURS — COMPLÉTUDE
// ============================================================================

/** Champs attendus pour un interlocuteur complet */
export interface InterlocuteurCompletude {
  interlocuteurId: string;
  type: 'syndic' | 'promoteur';
  raisonSociale: string;
  /** true si au moins email OU adresse est renseigné */
  contactComplet: boolean;
  /** Champs manquants */
  champsManquants: ('email' | 'adresse' | 'telephone' | 'contactPrincipal')[];
}
